/**
 * DragHandle.jsx
 *
 * A simple, non-interactive visual handle that indicates drag-and-drop capability for mouse users.
 *
 * This component forwards its ref to a <span> element that wraps a drag indicator icon.
 * It is styled with a grab cursor to indicate draggable behavior.
 *
 * Note: This component does not support keyboard interaction or accessibility features,
 * It is intended exclusively for mouse-based interactions.
 */

import { forwardRef, memo } from "react";
import Box from "@mui/material/Box";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

/**
 * @typedef {object} DragHandleProps
 * This component does not accept any props.
 */

/**
 * DragHandle
 * 
 * A visual drag handle used for mouse based drag and drop.
 * 
 * Uses React.forwardRef to pass the ref down to the <span> element.
 * This component is not interactive and is not focusable.
 *
 * @param {DragHandleProps} _ - Component props (unused)
 * @param {React.Ref<HTMLSpanElement>} ref - Ref forwarded to the span element
 * @returns {JSX.Element}
 */
const DragHandle = forwardRef((_, ref) => (
  <Box
    component="span"
    ref={ref}
    sx={{ cursor: "grab", display: "flex" }}
  >
    <DragIndicatorIcon />
  </Box>
));

DragHandle.propTypes = {};

DragHandle.displayName = "DragHandle";

export default memo(DragHandle);
